import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
  refresh: () => api.post('/auth/refresh')
};

// Users API
export const usersApi = {
  getAll: () => api.get('/users'),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  resetPassword: (id, newPassword) => api.post(`/users/${id}/reset-password`, { user_id: id, new_password: newPassword })
};

// Students API
export const studentsApi = {
  getAll: (params) => api.get('/students', { params }),
  getOne: (id) => api.get(`/students/${id}`),
  getOutstanding: (id) => api.get(`/students/${id}/outstanding`),
  create: (data) => api.post('/students', data),
  update: (id, data) => api.put(`/students/${id}`, data),
  bulkUpdate: (data) => api.put('/students/bulk-update', data),
  setLifecycle: (data) => api.put('/students/bulk-lifecycle', data),
  toggleStatus: (id) => api.patch(`/students/${id}/toggle-status`),
  delete: (id) => api.delete(`/students/${id}`),
  getKpiSummary: (params) => api.get('/students/summary/kpi', { params })
};

// Fee Structures API
export const feeStructuresApi = {
  getAll: (params) => api.get('/fee-structures', { params }),
  create: (data) => api.post('/fee-structures', data),
  update: (id, data) => api.put(`/fee-structures/${id}`, data),
  delete: (id) => api.delete(`/fee-structures/${id}`),
  bulkDelete: (ids) => api.post('/fee-structures/bulk-delete', { ids }),
  uploadExcel: (formData) => api.post('/fee-structures/upload-excel', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  })
};

// Invoices API
export const invoicesApi = {
  getAll: (params) => api.get('/invoices', { params }),
  create: (data) => api.post('/invoices', data),
  generateBulk: (data) => api.post('/invoices/generate-bulk', data),
  createMultiLine: (data) => api.post('/invoices/multi-line', data),
  checkDuplicate: (data) => api.post('/invoices/check-duplicate', data),
  bulkDelete: (ids) => api.post('/invoices/bulk-delete', { ids })
};

// Payments API
export const paymentsApi = {
  getAll: (params) => api.get('/payments', { params }),
  create: (data) => api.post('/payments', data),
  getReceipt: (id) => api.get(`/payments/${id}/receipt`),
  update: (id, data) => api.put(`/payments/${id}`, data),
  // Invoice mapping: read what a payment is marked against / re-map it onto a
  // different set of invoices (send the full desired set; omitted ones are released).
  getInvoiceMapping: (id) => api.get(`/payments/${id}/invoice-mapping`),
  updateInvoiceMapping: (id, invoiceIds) => api.put(`/payments/${id}/invoice-mapping`, { invoice_ids: invoiceIds }),
  bulkDelete: (ids) => api.post('/payments/bulk-delete', { ids })
};

// Families API
export const familiesApi = {
  getAll: (params) => api.get('/families', { params }),
  getOne: (id) => api.get(`/families/${id}`),
  create: (data) => api.post('/families', data),
  update: (id, data) => api.put(`/families/${id}`, data),
  delete: (id) => api.delete(`/families/${id}`),
  getStudents: (id) => api.get(`/families/${id}/students`),
  linkStudents: (id, studentIds) => api.post(`/families/${id}/students`, { student_ids: studentIds }),
  unlinkStudent: (id, studentId) => api.delete(`/families/${id}/students/${studentId}`)
};

// Discounts API
export const discountsApi = {
  getAll: (params) => api.get('/discounts', { params }),
  request: (data) => api.post('/discounts/request', data),
  approve: (id) => api.put(`/discounts/${id}/approve`),
  reject: (id, reason) => api.put(`/discounts/${id}/reject`, { reason }),
  delete: (id) => api.delete(`/discounts/${id}`)
};

// Expenses API
export const expensesApi = {
  getAll: (params) => api.get('/expenses', { params }),
  create: (data) => api.post('/expenses', data)
};

// Bank Transactions API
export const bankTransactionsApi = {
  getAll: (params) => api.get('/bank-transactions', { params }),
  create: (data) => api.post('/bank-transactions', data),
  reconcile: (id, data) => api.put(`/bank-transactions/${id}/reconcile`, data),
  moveToSuspense: (id) => api.put(`/bank-transactions/${id}/suspense`),
  update: (id, data) => api.put(`/bank-transactions/${id}`, data),
  // Invoice mapping of the payment(s) this statement line was reconciled into.
  getInvoiceMapping: (id) => api.get(`/bank-transactions/${id}/invoice-mapping`),
  bulkDelete: (ids) => api.post('/bank-transactions/bulk-delete', { ids })
};

// Uniforms API
export const uniformsApi = {
  getOrders: (params) => api.get('/uniforms/orders', { params }),
  createOrder: (data) => api.post('/uniforms/orders', data),
  updateOrderStatus: (id, status) => api.put(`/uniforms/orders/${id}/status`, { status }),
  getReorderSuggestions: () => api.get('/uniforms/reorder-suggestions')
};

// Tally API
export const tallyApi = {
  getSetup: () => api.get('/tally/setup'),
  saveSetup: (data) => api.post('/tally/setup', data),
  testConnection: (tallyType) => api.post('/tally/test-connection', { tally_type: tallyType }),
  post: (data) => api.post('/tally/post', data),
  getPostingHistory: (params) => api.get('/tally/posting-history', { params })
};

// Profile API
export const profileApi = {
  get: () => api.get('/profile'),
  update: (data) => api.put('/profile', data),
  changePassword: (data) => api.post('/profile/change-password', data)
};

// Reports API
export const reportsApi = {
  dashboard: (params) => api.get('/reports/dashboard', { params }),
  dashboardVersion: () => api.get('/reports/dashboard/version'),
  dashboardCreditNotes: (params) => api.get('/reports/dashboard/credit-notes', { params }),
  exportDashboardCreditNotesExcel: (params) => api.get('/reports/dashboard/credit-notes/excel', { params, responseType: 'blob' }),
  studentLedger: (id) => api.get(`/reports/student-ledger/${id}`),
  // Statement of account: PDF download + email-to-parent (PDF attached)
  studentLedgerPdf: (id) => api.get(`/reports/student-ledger/${id}/pdf`, { responseType: 'blob' }),
  emailStudentLedger: (id, to) => api.post(`/reports/student-ledger/${id}/email`, { to: to || null }),
  outstanding: (params) => api.get('/reports/outstanding', { params }),
  collectionSummary: (params) => api.get('/reports/collection-summary', { params }),
  discountAudit: () => api.get('/reports/discount-audit'),
  suspense: () => api.get('/reports/suspense'),
  dayBook: (params) => api.get('/reports/day-book', { params }),
  // New Admissions (term-wise intake, branch-split)
  newAdmissions: (params) => api.get('/reports/new-admissions', { params }),
  newAdmissionsStudents: (params) => api.get('/reports/new-admissions/students', { params }),
  // New Admission Report (Branch x Class head-count from a start date)
  newAdmissionReport: (params) => api.get('/reports/new-admission-report', { params }),
  newAdmissionReportStudents: (params) => api.get('/reports/new-admission-report/students', { params }),
  exportNewAdmissionReportExcel: (params) => api.get('/reports/new-admission-report/excel', { params, responseType: 'blob' }),
  // Registration Invoiced (admission cohort vs registration fee lines)
  registrationInvoiced: (params) => api.get('/reports/registration-invoiced', { params }),
  registrationInvoicedStudents: (params) => api.get('/reports/registration-invoiced/students', { params }),
  exportRegistrationInvoicedExcel: (params) => api.get('/reports/registration-invoiced/excel', { params, responseType: 'blob' }),
  // Term-wise financials (Branch x Term x FY) + its two drill-downs
  termFinancials: (params) => api.get('/reports/term-financials', { params }),
  termFinancialsInvoices: (params) => api.get('/reports/term-financials/invoices', { params }),
  termFinancialsCollections: (params) => api.get('/reports/term-financials/collections', { params }),
  termFinancialsCreditNotes: (params) => api.get('/reports/term-financials/credit-notes', { params }),
  termFinancialsOpening: (params) => api.get('/reports/term-financials/opening', { params }),
  termFinancialsClosing: (params) => api.get('/reports/term-financials/closing', { params }),
  exportTermFinancialsExcel: (params) => api.get('/reports/term-financials/excel', { params, responseType: 'blob' }),
  // Lunch / Club / Uniform registrations (FY x Term x Fee Head student counts)
  registrations: (params) => api.get('/reports/registrations', { params }),
  registrationsStudents: (params) => api.get('/reports/registrations/students', { params }),
  exportRegistrationsExcel: (params) => api.get('/reports/registrations/excel', { params, responseType: 'blob' }),
  // Excel exports
  exportOutstandingExcel: (params) => api.get('/reports/outstanding/excel', { params, responseType: 'blob' }),
  exportCollectionExcel: (params) => api.get('/reports/collection-summary/excel', { params, responseType: 'blob' }),
  exportDayBookExcel: (params) => api.get('/reports/day-book/excel', { params, responseType: 'blob' }),
  exportStudentsExcel: (params) => api.get('/reports/students/excel', { params, responseType: 'blob' }),
  exportNewAdmissionsExcel: (params) => api.get('/reports/new-admissions/excel', { params, responseType: 'blob' })
};

// Academic API
export const academicApi = {
  promote: (data) => api.post('/academic/promote', data),
  getStudentsForPromotion: (params) => api.get('/academic/students-for-promotion', { params })
};

// Config API
export const configApi = {
  get: () => api.get('/config'),
  getSystemInfo: () => api.get('/config/system-info')
};

// Audit Logs API
export const auditLogsApi = {
  getAll: (params) => api.get('/audit-logs', { params })
};

// Lunch Coupons API
export const lunchCouponsApi = {
  getAll: (params) => api.get('/lunch-coupons', { params }),
  mark: (data) => api.post('/lunch-coupons/mark', data),
  bulkMark: (data) => api.post('/lunch-coupons/bulk-mark', data),
  report: (params) => api.get('/lunch-coupons/report', { params })
};

// Club Attendance API
export const clubAttendanceApi = {
  getAll: (params) => api.get('/club-attendance', { params }),
  record: (data) => api.post('/club-attendance/record', data),
  bulkRecord: (data) => api.post('/club-attendance/bulk-record', data),
  report: (params) => api.get('/club-attendance/report', { params })
};

// Call Logs API
export const callLogsApi = {
  getAll: (params) => api.get('/call-logs', { params }),
  create: (data) => api.post('/call-logs', data),
  delete: (id) => api.delete(`/call-logs/${id}`)
};

// Credit Notes API
export const creditNotesApi = {
  getAll: (params) => api.get('/credit-notes', { params }),
  create: (data) => api.post('/credit-notes', data),
  approve: (id) => api.put(`/credit-notes/${id}/approve`),
  reject: (id) => api.put(`/credit-notes/${id}/reject`),
  getPdf: (id) => api.get(`/credit-notes/${id}/pdf`, { responseType: 'blob' }),
  bulkDelete: (ids) => api.post('/credit-notes/bulk-delete', { ids })
};

// Feedback Ticket API (voice/video feedback -> AI checklist -> ticket)
export const feedbackApi = {
  // `formData` is a multipart FormData (audio required; video/url/trail/shots optional)
  create: (formData) => api.post('/feedback', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  list: () => api.get('/feedback'),
  get: (id, sync) => api.get(`/feedback/${id}${sync ? '?sync=1' : ''}`),
  sync: (id) => api.post(`/feedback/${id}/sync`),
  setStatus: (id, body) => api.post(`/feedback/${id}/status`, body),
  retranscribe: (id) => api.post(`/feedback/${id}/retranscribe`),
  setCheck: (id, body) => api.post(`/feedback/${id}/checklist`, body),
  remove: (id) => api.delete(`/feedback/${id}`)
};

// Absolute base for media URLs returned by the feedback API (served by the backend,
// which may live on a different origin than the SPA). The serializer returns paths
// like "/api/feedback/file/<fid>/<name>"; prefix them with the backend origin.
export const FEEDBACK_MEDIA_BASE = API_URL || '';

export default api;
